/*
 * IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA
 * GOVERNMENT - GOVERNMENT - GOVERNMENT - GOVERNMENT - GOVERNMENT
 *
 * Copyright: 2023 by Idemia Identity & Security USA LLC. All rights reserved.
 * License: In accordance  Idemia I&S USA LLC's license agreement.
 * Code Classification: GOVERNMENT
 *
 * Classification Person: Nadim Bakizada nadim.bakizada@us.idemia.com
 * Classification Reason: Software not specific to any U.S. Government Entity
 * Classification Date: 2023
 *
 * GOVERNMENT - GOVERNMENT - GOVERNMENT - GOVERNMENT - GOVERNMENT
 * IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA
 */

import { formatDate, formatBirthDate } from "utils/formatDate";
import dayjs from "dayjs";
import { getFirstSSN } from "./formatSSN";
const advancedFormat = require('dayjs/plugin/advancedFormat')
dayjs.extend(advancedFormat)

const fieldValue = (field: string | number | undefined) => field ?? "N/A";

const displayHeightInFeet = (string: string | undefined) => {
  console.log("height string: ", string);
  let height = string ? `${string[0]}'${string.slice(1)}"` : "";
  console.log("height test: ", height);
  return height;
};

export type UserProfileInfo = ReturnType<typeof generateUserProfileInfo>;
export type PersonalInfo = ReturnType<typeof generatePersonalInformation>;
export type TransactionalInfo = ReturnType<typeof generateTransactionalInformation>;
export type RequestingAgencyInfo = ReturnType<typeof generateRequestingAgencyInfo>;
export type StatusHistories = ReturnType<typeof generateStatusHistories>;
export type EmploymentInfo = ReturnType<typeof generateEmploymentInformation>;
export type AliasInfo = ReturnType<typeof generateAliasInformation>;

interface Alias {
  firstName: string;
  middleName: string;
  lastName: string;
  suffix: string;
}

// applicant details header
export const generateUserProfileInfo = (data: object, applicantData: object, dobFormat: string) => {
  console.log('debug applicantData generateUserProfileInfo: ', data, applicantData)

  const fullName = `${data["firstName"] ?? ""} ${data["middleName"] ?? ""} ${data["lastName"] ?? ""} ${data["suffix"] ?? ""}`;
  console.log('debug applicantData generateUserProfileInfo config fullName: ', fullName)

  return {
    name: fieldValue(fullName),
    suffix: fieldValue(data["suffix"]),
    status: fieldValue(applicantData["attributes"].finalDecision || applicantData["attributes"].transactionStatus),
    ori: fieldValue(data["ori"] || applicantData["attributes"]['ORIG-AGENCY']),
    agency: fieldValue(data["agency"]),
    secondaryOri: fieldValue(data["secondaryOri"]),
    tcn: fieldValue(data["tcn"]),
    ssn: getFirstSSN(fieldValue(data["ssn"])),
    birthDate: formatBirthDate(fieldValue(data["birthDate"]) as string, dobFormat),
    placeOfBirth: fieldValue(data["placeOfBirth"]),
    race: fieldValue(data["race"]),
    gender: fieldValue(data["gender"]),
    height: displayHeightInFeet(fieldValue(data["height"]) as string),
    weight: ` ${fieldValue(data["weight"])} lbs`,
    hairColor: fieldValue(data["hairColor"]),
    eyeColor: fieldValue(data["eyeColor"])
  }
};

const assembleSplitAddress = (data: any) => {
  const address = (data["street"] && data["city"] && data["state"] && data["zipCode"]);
  if (address) {
    return `${data["street"]}, ${data["city"]}, ${data["state"]} ${data["zipCode"]}`
  } else {
    return "N/A"
  }
}

export const generatePersonalInformation = (data: object, dobFormat: string) => {
  console.log(`personalInfo generatePersonalInformation applicantData ${JSON.stringify(data, null, "\t")}`)

  return {
    firstName: fieldValue(data["firstName"]),
    middleName: fieldValue(data["middleName"]),
    lastName: fieldValue(data["lastName"]),
    suffix: fieldValue(data["suffix"]),
    residenceAddress: data["fullAddress"] ? modifyAddress(data["fullAddress"]) : assembleSplitAddress(data),
    mailingAddress: modifyAddress(data["mailingAddress"]),
    ssn: fieldValue(data["ssn"]),
    birthDate: formatBirthDate(fieldValue(data["birthDate"]) as string, dobFormat),
    placeOfBirth: fieldValue(data["placeOfBirth"]),
    countryOfCitizenship: fieldValue(data["countryOfCitizenship"]),
    race: fieldValue(data["race"]),
    gender: fieldValue(data["gender"]),
    height: displayHeightInFeet(fieldValue(data["height"]) as string),
    weight: `${fieldValue(data["weight"])} lbs`,
    hairColor: fieldValue(data["hairColor"]),
    eyeColor: fieldValue(data["eyeColor"]),
    aliasFirstName: fieldValue(data["aliasFirstName"]),
    aliasMiddleName: fieldValue(data["aliasMiddleName"]),
    aliasLastName: fieldValue(data["aliasLastName"]),
    aliasSuffix: fieldValue(data["aliasSuffix"]),
    applicantEmail: fieldValue(data["applicantEmail"]),
    applicantPhone: fieldValue(data["applicantPhone"])
  };
};

export const generateAliasInformation = (data: object) => {
  if(data["alias"]){
    return{
      aliases: parseAlias(fieldValue(data["alias"]))
    }
  } else {
    return {
      aliasFirstName: fieldValue(data["aliasFirstName"]),
      aliasMiddleName: fieldValue(data["aliasMiddleName"]),
      aliasLastName: fieldValue(data["aliasLastName"]),
      aliasSuffix: fieldValue(data["aliasSuffix"])
    };
  }
};

export const generateTransactionalInformation = (data: object, applicantData: object) => {
  console.log('transactionalInfo applicantData: ', applicantData, data)

  const fileName = getPdfFileName(applicantData["attachments"]);
  console.log('debug filename: ', fileName);

  console.log(
    "debug applicantData applicantData.datelastmodified ?? '' :",
    formatDate(applicantData["datelastmodified"]) ?? ""
  );

  return {
    ori: fieldValue(data["ori"]),
    secondaryOri: fieldValue(data["secondaryOri"]),
    UEID: fieldValue(data["ueid"]),
    incomingTCN: fieldValue(data["incomginTCN"]), //this is tcr check
    transactionNumber: fieldValue(data["tcn"]),
    transactionType: fieldValue(data["transactionType"]),
    assignedAgent: fieldValue("N/A"),
    lastModifiedBy: formatDate(applicantData["datelastmodified"]) ?? "N/A",
    statusTimeout: fieldValue(""),
    transactionTimeout: fieldValue(""),
    reasonFingerprinted: fieldValue(data["reasonFingerprinted"]),
    agency: fieldValue(data["agencyName"]),
    fileName: fileName[0],
    agencyIdentifier: "N/A",
    currentStatus: fieldValue(
      applicantData["attributes"].finalDecision ||
      applicantData["attributes"].transactionStatus
    ),
    pastResidentialAddressList:data['pastResidentialAddressList'] ?? "N/A",
    cardType: data["cardType"] ?? undefined
  };
};

export const generateRequestingAgencyInfo = (data: object) => {
  return {
    requestingAgencyOri: fieldValue(data["secondaryOri"]),
    requestingAgencyName: fieldValue(data["requestingAgencyName"]),
    phoneNumber: "N/A",
    emailAddress: fieldValue(data["emailAddress"]),
    address: fieldValue(data["address"]),
    city: fieldValue(data["city"]),
    state: fieldValue(data["state"]),
    zipCode: fieldValue(data["zipCode"])
  }
}

const EMPLOYER_FIELD_MAP: Record<number, (get: (i: number) => string) => Record<string, string>> = {
  4: (get) => ({ employerName: "N/A", employerAddress: get(0), employerCity: "N/A", employerState: get(1), employerZipCode: get(3), employerCountry: get(2) }),
  5: (get) => ({employerName: get(0), employerAddress: get(1), employerCity: get(2), employerState: get(3), employerZipCode: get(4), employerCountry: get(5)}),
  6: (get) => ({ employerName: get(0), employerAddress: get(1), employerCity: get(2), employerState: get(3), employerZipCode: get(4), employerCountry: get(5) }),
  7: (get) => ({ employerName: get(0), employerAddress: [get(1), get(2)].filter(p => p !== "N/A").join(" "), employerCity: get(3), employerState: get(4), employerZipCode: get(5), employerCountry: get(6) }),
};
 
const valueCheck = (value: string | undefined) => value?.trim() || "N/A"; 

const resolveEmployerAddress = (data: any) => {

  console.log("Debug Employer Info Data: ", data);

  if (data["employerInfo"] && data["employerInfo"] !== "N/A") {
    const parts = data["employerInfo"].split(",").map((p: string) => p.trim());
    const get = (i: number) => parts[i] ?? "N/A";
    return EMPLOYER_FIELD_MAP[parts.length]?.(get)
  }
 
  return {
    employerName: valueCheck(data["employerName"]),
    employerAddress: valueCheck([data["employerStreet"], data["employerStreet2"]].filter(Boolean).join(" ")),
    employerCity: valueCheck(data["employerCity"]),
    employerState: valueCheck(data["employerState"]),
    employerZipCode: valueCheck(data["employerZipCode"]),
    employerCountry: valueCheck(data["employerCountry"])
  };
};

const parseAlias = (input: any): Alias[] => {
  if (!input) return [];

  const suffixList = ['JR', 'SR', 'I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX'];

  const entries = Array.isArray(input)
    ? input
    : [{ V1_field19: input }];

  return entries.map(({ V1_field19 }: { V1_field19: string }) => {
    const [lastPartRaw, restRaw] = V1_field19.split(',').map(part => part.trim());

    const nameParts = restRaw ? restRaw.trim().split(/\s+/) : [];

    const hasPotentialSuffix = nameParts.length > 1;
    const lastWord = hasPotentialSuffix ? nameParts[nameParts.length - 1].toUpperCase() : '';

    const isSuffix = suffixList.includes(lastWord);

    const suffix = isSuffix ? nameParts.pop() || '' : '';
    const firstName = nameParts[0] || '';
    const middleName = nameParts.slice(1).join(' ');

    return {
      firstName,
      middleName,
      lastName: lastPartRaw || '',
      suffix
    };
  });
};
 
export const generateEmploymentInformation = (data: any) => ({
  occupation: fieldValue(data["occupation"]),
  ...resolveEmployerAddress(data),
});

export const generateAggregatedResponses = (applicantData: any) => {
  return applicantData.descriptors.data.type2.field75;
};

const modifyAddress = (address: string) => {
  let modifiedAddress
  if (address) {
    modifiedAddress = address?.replace(/,/g, ', ');
    modifiedAddress = modifiedAddress.trim().replace(/,\s*$/, '');
    return modifiedAddress;
  } else {
    return "N/A"
  }
};

const getPdfFileName = (data: any) => {
  return data
    .map(attachment => attachment.attributes?.fileName)
    .filter(Boolean);
}

export const generateStatusHistories = (applicantData: any, index: any) => {
  console.log(
    "debug data applicantData in generateStatusHistories: ",
    applicantData
  );
  return {
    key: index,
    id: applicantData.uuid,
    status: applicantData.status,
    statusDate: applicantData.insert_date
      ? dayjs(applicantData.insert_date).format("MM/DD/YYYY hh:mm:ss A")
      : "",
    agency: applicantData.agency,
  };
};

export const parseName = (name: string) => {
  console.log(`parseName name: ${name}`)
  let arr = name.split(",")
  console.log(`parseName arr: `, arr)
  let firstName, middleName
  let lastName = arr[0]
  let firstMiddle = arr[1].trim().split(" ")

  firstName = firstMiddle[0]
  middleName = firstMiddle[1]
  return [firstName, middleName, lastName]
}
